import type { SourceStatement } from '../types';

/**
 * Conversion fix #3 (spec docs/superpowers/specs/2026-10-21-duplicate-statement-warning-design.md):
 * pairs of source statements that look like the SAME sales. The draft sums every statement on purpose
 * (a client can genuinely have several), so this changes no number. It only stops a doubled figure
 * from passing silently.
 *
 * Flag a pair when the file names match (ignoring case and surrounding space), or when both report
 * grossSales > 0 within OVERLAP_PCT of each other. 5% was measured on the 2026-10-20 sweep: it caught
 * 8 of 16 multi-statement overlaps and flagged 0 of 12 legitimate multi-statement filings.
 */
export const OVERLAP_PCT = 0.05;

export interface StatementOverlap {
  a: SourceStatement;
  b: SourceStatement;
  reason: 'sameName' | 'similarGross';
}

export function findLikelyDuplicates(statements: SourceStatement[], pct = OVERLAP_PCT): StatementOverlap[] {
  const out: StatementOverlap[] = [];
  for (let i = 0; i < statements.length; i++) {
    for (let j = i + 1; j < statements.length; j++) {
      const a = statements[i];
      const b = statements[j];
      const na = (a.sourceFileName ?? '').trim().toLowerCase();
      const nb = (b.sourceFileName ?? '').trim().toLowerCase();
      if (na && na === nb) {
        out.push({ a, b, reason: 'sameName' });
        continue;
      }
      const ga = a.figures?.grossSales;
      const gb = b.figures?.grossSales;
      if (typeof ga === 'number' && typeof gb === 'number' && ga > 0 && gb > 0
        && Math.abs(ga - gb) / Math.max(ga, gb) <= pct) {
        out.push({ a, b, reason: 'similarGross' });
      }
    }
  }
  return out;
}
